import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeaderContainer = styled(motion.header)<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled 
    ? 'rgba(8, 11, 16, 0.88)' 
    : 'rgba(8, 11, 16, 0.62)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: ${props => props.scrolled 
    ? '1px solid rgba(0, 212, 255, 0.2)' 
    : '1px solid rgba(255, 255, 255, 0.05)'};
  padding: 1.2rem 0;
  transition: all 0.3s ease;
  box-shadow: ${props => props.scrolled 
    ? '0 4px 30px rgba(0, 0, 0, 0.3)' 
    : 'none'};

  @media (max-width: 768px) {
    padding: 0.75rem 0;
  }
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    flex-wrap: nowrap;
    gap: 0.75rem;
  }

  @media (max-width: 380px) {
    padding: 0 12px;
  }
`;

const Logo = styled(motion.div)`
  font-size: 1.18rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff, #7affc8 52%, #67d8ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #00d4ff, #0099cc);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.35rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    margin-left: auto;
    flex-shrink: 0;
    padding: 3px;
    background: rgba(128, 230, 255, 0.045);
    border: 1px solid rgba(128, 230, 255, 0.14);
    border-radius: 12px;
  }
`;

const VisitorStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding-left: 0.9rem;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 0.65rem;
    border-left: 0;
    gap: 0.65rem;
  }

  @media (max-width: 380px) {
    gap: 0.5rem;
  }
`;

const VisitorStat = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  color: #8fa3b8;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;

  strong {
    color: #7affc8;
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0;
  }

  @media (max-width: 380px) {
    font-size: 0.58rem;

    strong {
      font-size: 0.68rem;
    }
  }
`;

const NavLink = styled(motion.a)`
  color: #dbe8f3;
  font-weight: 700;
  font-size: 0.88rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  padding: 0.55rem 0.78rem;
  border-radius: 8px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #7affc8, #67d8ff);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #00d4ff;
    background: rgba(128, 230, 255, 0.08);
    
    &::after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  flex-shrink: 0;
  margin-left: auto;
  color: #ffffff;
  font-size: 1.8rem;
  background: rgba(128, 230, 255, 0.1);
  border: 1px solid rgba(128, 230, 255, 0.24);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
  }
  
  @media (max-width: 768px) {
    display: grid;
    place-items: center;
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    margin-left: 0;
    font-size: 1.4rem;
    line-height: 1;
    padding: 0;
    background: transparent;
    border-color: transparent;
    border-left-color: rgba(128, 230, 255, 0.14);
    border-radius: 0 8px 8px 0;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(8, 11, 16, 0.98);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  z-index: 999;
`;

const MobileNavLink = styled(motion.a)`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 0.5rem 1.5rem;
  border-radius: 10px;
  position: relative;
  
  &:hover {
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.1);
  }
`;

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visitorCounts, setVisitorCounts] = useState<{ today: number; total: number } | null>(null);
  const [visitorLoadFailed, setVisitorLoadFailed] = useState(false);
  const visitorRequestStarted = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (visitorRequestStarted.current) return;
    visitorRequestStarted.current = true;

    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
    const visitKey = `popol-visit-${today}`;
    let hasCountedVisit = false;
    try {
      hasCountedVisit = window.sessionStorage.getItem('popol-visit-counted') === visitKey;
    } catch {
      // Keep the site usable when browser storage is disabled.
    }

    fetch(`/api/visitors${hasCountedVisit ? '' : '?track=1'}`)
      .then((response) => {
        if (!response.ok) throw new Error('조회수를 불러오지 못했습니다.');
        return response.json() as Promise<{ today: number; total: number }>;
      })
      .then((counts) => {
        setVisitorCounts(counts);
        if (!hasCountedVisit) {
          try {
            window.sessionStorage.setItem('popol-visit-counted', visitKey);
          } catch {
            // Counting still works when browser storage is disabled.
          }
        }
      })
      .catch(() => {
        setVisitorLoadFailed(true);
      });
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'about', label: '소개' },
    { id: 'skills', label: '기술' },
    { id: 'projects', label: '프로젝트' },
    { id: 'certifications', label: '자격증' },
    { id: 'education', label: '학력' },
    { id: 'contact', label: '연락처' }
  ];

  return (
    <>
      <HeaderContainer
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        scrolled={isScrolled}
      >
        <Nav>
          <Logo 
            onClick={() => scrollToSection('hero')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Kim Subin
          </Logo>
          
          <NavLinks>
            {navItems.map((item, index) => (
              <NavLink
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          <HeaderActions>
          <VisitorStats aria-label="사이트 조회수" aria-live="polite">
            <VisitorStat>오늘 <strong>{visitorCounts ? new Intl.NumberFormat('ko-KR').format(visitorCounts.today) : visitorLoadFailed ? '오류' : '…'}</strong></VisitorStat>
            <VisitorStat>누적 <strong>{visitorCounts ? new Intl.NumberFormat('ko-KR').format(visitorCounts.total) : visitorLoadFailed ? '오류' : '…'}</strong></VisitorStat>
          </VisitorStats>
          
          <MobileMenuButton 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isMobileMenuOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d={isMobileMenuOpen ? 'M6 6l12 12M6 18L18 6' : 'M4 6h16M4 12h16M4 18h16'} />
            </svg>
          </MobileMenuButton>
          </HeaderActions>
        </Nav>
      </HeaderContainer>

      {isMobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {navItems.map((item, index) => (
            <MobileNavLink
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {item.label}
            </MobileNavLink>
          ))}
        </MobileMenu>
      )}
    </>
  );
};

export default Header; 
